<template lang="pug">
  //- <noindex> keeps search engines (Yandex) from indexing this helper bar
  noindex.country-nudge-noindex
    .country-nudge-banner(v-if="show" ref="bar")
      .country-nudge-content
        p.country-nudge-text
          span {{ text.prompt }}
          span.country-nudge-country  {{ text.name }}
          span.country-nudge-flag
            img.country-nudge-emoji(:src="flagUrl" :alt="config.flag" loading="lazy")
          span {{ text.question }}
        a.country-nudge-button(:href="config.url") {{ text.button }}
      button.country-nudge-close(type="button" aria-label="Close" @click="dismiss")
        svg(xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2")
          line(x1="18" y1="6" x2="6" y2="18")
          line(x1="6" y1="6" x2="18" y2="18")
</template>

<script>
import { mapState, mapActions } from 'vuex'

import { countryNudgeConfig, globalFallback, siteCodeToIso } from '@/constants/countryNudge'

export default {
  name: 'CountryNudgeBanner',
  data: () => ({
    dismissed: sessionStorage.getItem('countryNudgeDismissed') === 'true'
  }),
  computed: {
    ...mapState('main', ['clientLocation']),
    ...mapState('siteVersions', { siteVersion: 'item' }),
    // Visitor country from geoIP (ISO uppercase). 'GLOBAL' = geoIP could not resolve it.
    detected: vm => (vm.clientLocation || 'global').toUpperCase(),
    // Current site version country (ISO) — so we don't nudge toward where the user already is.
    currentCountry: vm => siteCodeToIso[vm.siteVersion.code] || null,
    configKey: vm => countryNudgeConfig[vm.detected] ? vm.detected : 'GLOBAL',
    config: vm => countryNudgeConfig[vm.detected] || globalFallback,
    // Render the flag as a twemoji SVG (jsdelivr) — emoji flags don't render in Chrome/Edge on Windows.
    flagUrl: vm => {
      const codes = [...vm.config.flag].map(ch => ch.codePointAt(0).toString(16)).join('-')
      return `https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/svg/${codes}.svg`
    },
    text: vm => ({
      name: vm.$t(`countryNudge.${vm.configKey}.name`),
      prompt: vm.$t(`countryNudge.${vm.configKey}.prompt`),
      question: vm.$t(`countryNudge.${vm.configKey}.question`),
      button: vm.$t(`countryNudge.${vm.configKey}.button`)
    }),
    show: vm =>
      !vm.dismissed &&
      // Never show on the Russian site version (it has its own promo banner).
      vm.currentCountry !== 'RU' &&
      vm.detected && vm.detected !== 'GLOBAL' &&
      vm.currentCountry && vm.currentCountry !== 'GLOBAL' &&
      vm.detected !== vm.currentCountry
  },
  watch: {
    show: {
      immediate: true,
      handler (val) {
        if (val) {
          // Add the class immediately (fallback menu shift kicks in); refine the exact height after render.
          document.documentElement.classList.add('country-nudge-active')
          this.$nextTick(() => {
            this.startMeasure()
            this.fitBanner()
          })
        } else {
          this.clearOffset()
        }
      }
    }
  },
  created () {
    // On self-hosted domains App.vue doesn't request geoIP — fetch it ourselves (ipapi, works in RU too).
    if (!this.clientLocation || this.clientLocation === 'global') {
      this.getClientLocation()
    }
  },
  beforeDestroy () {
    this.clearOffset()
  },
  methods: {
    ...mapActions('main', ['getClientLocation']),
    // The menu (.header__top) is pinned to top:0 and overlaps the bar — push it down by the bar height.
    // ResizeObserver keeps --country-nudge-height exact (text wrap, flag load, etc.).
    startMeasure () {
      const bar = this.$refs.bar
      if (!bar) return
      this.measure()
      window.addEventListener('resize', this.fitBanner)
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(() => this.measure())
        this.ro.observe(bar)
      } else {
        window.addEventListener('resize', this.measure)
      }
    },
    // Shrink the text/button font just enough so the whole row always stays on ONE line.
    fitBanner () {
      const bar = this.$refs.bar
      if (!bar) return
      const content = bar.querySelector('.country-nudge-content')
      const textEl = bar.querySelector('.country-nudge-text')
      const btnEl = bar.querySelector('.country-nudge-button')
      if (!content || !textEl) return
      textEl.style.fontSize = ''
      if (btnEl) btnEl.style.fontSize = ''
      const cs = window.getComputedStyle(bar)
      const avail = bar.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)
      const t0 = parseFloat(window.getComputedStyle(textEl).fontSize)
      const b0 = btnEl ? parseFloat(window.getComputedStyle(btnEl).fontSize) : 0
      let f = 1
      let guard = 0
      while (content.scrollWidth > avail && f > 0.55 && guard < 40) {
        f -= 0.05
        guard++
        textEl.style.fontSize = `${t0 * f}px`
        if (btnEl) btnEl.style.fontSize = `${b0 * f}px`
      }
    },
    measure () {
      const bar = this.$refs.bar
      if (bar && bar.offsetHeight) {
        document.documentElement.style.setProperty('--country-nudge-height', `${bar.offsetHeight}px`)
      }
    },
    clearOffset () {
      if (this.ro) { this.ro.disconnect(); this.ro = null }
      window.removeEventListener('resize', this.measure)
      window.removeEventListener('resize', this.fitBanner)
      document.documentElement.classList.remove('country-nudge-active')
      document.documentElement.style.removeProperty('--country-nudge-height')
    },
    dismiss () {
      this.dismissed = true
      sessionStorage.setItem('countryNudgeDismissed', 'true')
    }
  }
}
</script>

<style scoped>
.country-nudge-noindex { display: block; }
.country-nudge-banner {
  position: relative;
  z-index: 70;
  background: #000;
  color: #fff;
  padding: 9px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: nudgeSlideDown 0.4s ease-out;
}
/* Concave ("outward") corners: black flows down at the edges and curves into the menu, framing it. */
.country-nudge-banner::before,
.country-nudge-banner::after {
  content: '';
  position: absolute;
  top: 100%;
  width: 22px;
  height: 22px;
  pointer-events: none;
}
.country-nudge-banner::before {
  left: 0;
  background: radial-gradient(circle at 100% 100%, transparent 22px, #000 22px);
}
.country-nudge-banner::after {
  right: 0;
  background: radial-gradient(circle at 0 100%, transparent 22px, #000 22px);
}
.country-nudge-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
  justify-content: center;
  max-width: 100%;
}
.country-nudge-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  flex-wrap: nowrap;
  white-space: nowrap;
  color: #fff;
  text-transform: none;
}
.country-nudge-country { font-weight: 600; }
.country-nudge-flag {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.country-nudge-emoji {
  width: 20px;
  height: 20px;
  margin: 0 2px;
  vertical-align: middle;
}
/* Button inherits the main site's .more-btn look: black bg, primary-colour border, uppercase.
   Padding is in em so it scales with the auto-fit font size (keeps it proportional on mobile). */
.country-nudge-button {
  background-color: #000;
  color: #fff;
  padding: 0.4em 1.15em;
  border: 2px solid var(--primary-color);
  border-radius: 30px;
  font-size: 14px;
  font-weight: 400;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  transition: 0.2s;
}
.country-nudge-button:hover { background-color: var(--primary-color); color: #000; }
.country-nudge-close {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}
.country-nudge-close:hover { opacity: 1; }

@keyframes nudgeSlideDown {
  from { opacity: 0; transform: translateY(-100%); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
  .country-nudge-banner  { padding: 8px 34px 8px 14px; }
  .country-nudge-content { gap: 10px; }
  .country-nudge-text    { font-size: 13px; }
  /* font-size only; padding stays em-based so it scales down with the text (no oversized button). */
  .country-nudge-button  { font-size: 12px; flex-shrink: 0; }
  .country-nudge-close   { right: 8px; }
}
</style>

<style>
/* Global (not scoped): the bar sits in flow ABOVE the menu and pushes it down by exactly its height.
   Fallback 48px — in case JS hasn't measured the real height yet.
   !important is required: HeaderTop.vue sets an inline top:0 via anime.js (entry animation), and an
   inline style beats a normal CSS rule — an !important (author) rule wins over a normal inline style.
   We don't touch .hidden-header (sticky header on scroll): the in-flow bar scrolls away on its own. */
.country-nudge-active .header__top {
  top: var(--country-nudge-height, 48px) !important;
}
</style>
